import Footer from '~/components/Footer'
import Navigation from '~/components/Navigation'
import { HEJA_STATS } from '~/config/stats'
import useTranslator from '~/lib/useTranslator'
import { mergeMeta } from '~/utils/mergeMeta'

export const meta = mergeMeta(() => [{ title: 'Heja | Partners' }])

const partners = [
  {
    logoUrl: '/images/logo_veo.svg',
    alt: 'Veo Sports Camera',
    id: 'veo',
    width: 128,
  },
  {
    logoUrl: '/images/logo_ncsa.svg',
    alt: 'NCSA College Recruiting',
    id: 'ncsa',
    width: 196,
  },
  {
    logoUrl: '/images/logo_grover.svg',
    alt: 'Grover',
    id: 'grover',
    width: 178,
  },
  {
    logoUrl: '/images/logo_snapraise.svg',
    alt: 'Snap! Raise',
    id: 'snap',
    width: 291,
  },
  {
    logoUrl: '/images/logo_cluey.svg',
    alt: 'Cluey Learning',
    id: 'cluey',
    width: 148,
  },
]

export default function Partners() {
  const Translator = useTranslator()

  return (
    <>
      <Navigation />
      <header className="bg-primary-color py-12">
        <div className="max-w-5xl mx-auto flex flex-col gap-6 md:flex-row items-center text-center md:text-left">
          <img
            src="/images/header-image.svg"
            role="presentation"
            width={530}
            height={438}
            alt="Heja sports team graphic"
            className="flex flex-1"
          />
          <div className="flex flex-1 flex-col gap-4 [&>p]:text-white [&>p]:text-lg px-8">
            <h1 className="text-4xl md:text-6xl text-white">
              {Translator.singular('We make our users your fans')}
            </h1>
            <p className="md:hidden ">
              {Translator.singular(
                'Connect with a highly engaged audience of families with direct access to the Chief Household Officer.'
              )}
            </p>
            <p className="hidden md:block ">
              {Translator.singular(
                'Heja is a rapidly growing Youth Sports platform where our partners are able to connect with a highly engaged audience of families with direct access to the Chief Household Officer.'
              )}
            </p>
            <p className="hidden md:block ">
              {Translator.singular(
                'Through our ad-formats, brands are able scale their business through connecting with more than'
              )}
              <b>{Translator.singular(' +4 million users. ')}</b>
              {Translator.singular(
                'Book a meeting with our team today to understand what value Heja will bring to your digital marketing strategies.'
              )}
            </p>
            <div className="flex justify-center md:justify-start">
              {/* <BookMeetingButton /> */}
            </div>
          </div>
        </div>
      </header>

      <div className="bg-gradient-to-b from-primary-color to-white from-50% to-50%">
        <section className="bg-primary-color-dark rounded-2xl shadow-lg overflow-visible mx-4 md:mx-auto md:max-w-5xl">
          <ul className="text-white md:grid md:grid-cols-3">
            <li className="py-6 px-2 border-b border-white/10 flex flex-col gap-1 items-center">
              <span className="font-medium text-2xl">
                {Translator.singular('{{ noOfTeams }} Teams', { noOfTeams: HEJA_STATS.teamCount })}
              </span>
              {Translator.singular('use Heja.')}
            </li>
            <li className="py-6 px-2 border-b border-white/10 flex flex-col gap-2 items-center justify-center">
              <svg className="h-5">
                <use xlinkHref="#totalstars" />
              </svg>
              <div>
                <strong>{HEJA_STATS.appStoreRating}</strong> {Translator.singular('from')}{' '}
                <strong>{HEJA_STATS.appStoreRatingCount}</strong> {Translator.singular('ratings')}
              </div>
            </li>
            <li className="py-6 px-2 flex flex-col gap-1 items-center">
              <span className="font-medium text-2xl">{Translator.singular('50+ Countries')}</span>
              {Translator.singular('and growing.')}
            </li>
          </ul>
        </section>
      </div>

      <main className="bg-white">
        <div className="py-12">
          <div className="max-w-5xl mx-auto flex gap-10 flex-col md:flex-row px-12 items-center text-center">
            <img
              src="/images/partners.png"
              width={430}
              height={430}
              alt="Examples of partnership integrations"
            />
            <div className="flex flex-col gap-4">
              <h2 className="text-4xl text-center md:text-left">
                {Translator.singular('What partnerships can do for you')}
              </h2>
              <p className="text-black/80 font-normal text-center md:text-left max-w-screen-md mx-auto">
                {Translator.singular(
                  'Tap into a captive digital audience of coaches, parents, and players without competing for their attention. Heja provides the ability to target campaigns toward the right audience for your brand. We have recently partnered with large companies such as Snap! Raise, Veo, Grover, and NCSA College Recruiting.'
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-100 md:rounded-3xl md:mx-auto max-w-5xl">
          <div className="flex flex-col gap-4 items-center py-8">
            <h2 className="text-4xl">{Translator.singular('Our partners')}</h2>
            <p className="text-black/80 text-lg font-normal text-center px-12 max-w-screen-md mx-auto">
              {Translator.singular(
                'Join our expanding group of partners that are interested in promoting their brand and providing value to nearly 2 Million Heja users.'
              )}{' '}
            </p>
            <div className="grid grid-cols-2 grid-rows-3 gap-4 px-12 md:grid-cols-5 md:grid-rows-1">
              {partners.map((partner) => (
                <span
                  key={partner.id}
                  id={partner.id}
                  className="flex justify-center items-center md:p-6"
                >
                  <img
                    src={partner.logoUrl}
                    alt={partner.alt}
                    key={partner.logoUrl}
                    width={partner.width}
                    height={68}
                    className="md:w-24"
                  />
                </span>
              ))}
            </div>
          </div>
        </div>
      </main>

      <section className="md:bg-white bg-transparent py-12">
        <div className="px-6 py-4 shadow-lg mx-6 rounded-xl md:mx-auto max-w-screen-md bg-white md:shadow-none">
          <div className="text-center">
            <h4 className="font-normal text-black/80">
              &ldquo;We have really enjoyed working with Heja and plan on extending our initial
              partnership with them. Not only have they only shown a true interest in our success,
              but we have also seen improved results when it comes to native ads.&rdquo;
            </h4>
            <div className="flex flex-col items-center mt-5">
              <span className="font-medium text-xl">Rob Newman</span>
              <span>
                Growth Marketing Manager
                <br />
                at Cluey Learning
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* <div className="px-4 md:bg-white pb-24">
        <GetInTouch />
      </div> */}

      <Footer />
    </>
  )
}
